import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const ConfigTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [superuserConfig, setSuperuserConfig] = useState({
    username: "",
    password: "",
    nombre: "",
    email: ""
  });
  const [loadingSuperuser, setLoadingSuperuser] = useState(false);

  const fetchSuperuser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/superuser`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setSuperuserConfig({
        username: response.data.username || "",
        password: "",
        nombre: response.data.nombre || "",
        email: response.data.email || ""
      });
    } catch (e) {
      console.error("Error fetching superuser:", e);
    }
  }, [credentials]);

  const handleSaveSuperuser = async () => {
    setLoadingSuperuser(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (superuserConfig.username) params.append('username', superuserConfig.username);
      if (superuserConfig.password) params.append('password', superuserConfig.password);
      if (superuserConfig.nombre) params.append('nombre', superuserConfig.nombre);
      params.append('email', superuserConfig.email || '');
      
      await axios.put(`${API}/admin/superuser?${params.toString()}`, {}, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      
      alert('Superusuario actualizado correctamente. Si cambiaste las credenciales, deberás iniciar sesión de nuevo.');
      setSuperuserConfig(prev => ({ ...prev, password: "" }));
    } catch (err) {
      setError(err.response?.data?.detail || "Error al actualizar superusuario");
    }
    setLoadingSuperuser(false);
  };

  useEffect(() => {
    fetchSuperuser();
  }, [fetchSuperuser]);

  return (
    <>
          <>
            <div className="form-section mb-8">
              <h3 className="form-title mb-6 flex items-center gap-2">
                <Settings size={20} className="text-yellow-400" />
                Configuración del Superusuario
              </h3>
              <p className="text-[var(--text-muted)] text-sm mb-6">
                Aquí puedes modificar las credenciales del administrador principal. 
                Si cambias el usuario o contraseña, deberás iniciar sesión de nuevo con las nuevas credenciales.
              </p>
              
              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 mb-6">
                  {error}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="form-label">Nombre de Usuario</label>
                  <input
                    type="text"
                    className="custom-input"
                    placeholder="Usuario de acceso"
                    value={superuserConfig.username}
                    onChange={(e) => setSuperuserConfig(prev => ({ ...prev, username: e.target.value }))}
                    data-testid="superuser-username"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Este será el usuario para acceder al panel de administración
                  </p>
                </div>
                <div className="form-group">
                  <label className="form-label">Nueva Contraseña</label>
                  <PasswordInput
                    placeholder="Dejar en blanco para no cambiar"
                    value={superuserConfig.password}
                    onChange={(e) => setSuperuserConfig(prev => ({ ...prev, password: e.target.value }))}
                    testId="superuser-password"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Solo introduce una contraseña si deseas cambiarla
                  </p>
                </div>
                <div className="form-group">
                  <label className="form-label">Nombre Completo</label>
                  <input
                    type="text"
                    className="custom-input"
                    placeholder="Nombre del administrador"
                    value={superuserConfig.nombre}
                    onChange={(e) => setSuperuserConfig(prev => ({ ...prev, nombre: e.target.value }))}
                    data-testid="superuser-nombre"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email de Contacto</label>
                  <input
                    type="email"
                    className="custom-input"
                    placeholder="admin@ejemplo.com"
                    value={superuserConfig.email}
                    onChange={(e) => setSuperuserConfig(prev => ({ ...prev, email: e.target.value }))}
                    data-testid="superuser-email"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex gap-4">
                <button
                  onClick={handleSaveSuperuser}
                  disabled={loadingSuperuser}
                  className="btn-primary px-8"
                  data-testid="save-superuser-btn"
                >
                  {loadingSuperuser ? (
                    <>
                      <span className="spinner-small mr-2"></span>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} className="inline mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </button>
                <button
                  onClick={() => fetchSuperuser()}
                  className="btn-download px-6"
                  data-testid="reload-superuser-btn"
                >
                  <RotateCcw size={18} className="inline mr-2" />
                  Recargar
                </button>
              </div>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 mt-4">
              <h4 className="text-yellow-400 font-bold uppercase text-sm mb-2 flex items-center gap-2">
                <AlertTriangle size={16} />
                Información de Seguridad
              </h4>
              <ul className="text-[var(--text-muted)] text-sm space-y-1">
                <li>• Las credenciales del superusuario dan acceso completo al sistema.</li>
                <li>• Utiliza contraseñas fuertes con al menos 8 caracteres, números y símbolos.</li>
                <li>• Después de cambiar las credenciales, se cerrará tu sesión automáticamente.</li>
                <li>• Guarda tus nuevas credenciales en un lugar seguro.</li>
              </ul>
            </div>
          </>

    </>
  );
};

export default ConfigTab;
